import {
  useAccountTags,
  useAddAccountTag,
  useRemoveAccountTag,
  useRenameAccountTag,
  useSetActiveAccountTags,
} from "@/api/hooks/account";
import { TagGroupWithAdd } from "@/components/organisms/TagGroupWithAdd/TagGroupWithAdd";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { useAppDispatch } from "@/reducer";
import { removeTagFromAllProfiles, renameTagInAllProfiles } from "@/reducer/ProfileState";
import { t } from "@lingui/core/macro";
import { Tag } from "@mm/metx-design-system";

export function DashboardTagFilter({ isSideNav = false }) {
  const addTagMutation = useAddAccountTag();
  const removeTagMutation = useRemoveAccountTag();
  const renameTagMutation = useRenameAccountTag();
  const setActiveTagsMutation = useSetActiveAccountTags();
  const desktop = useDesktop();
  const dispatch = useAppDispatch();
  const { tags, isLoading: tagsLoading } = useAccountTags();

  const handleToggleTag = (tag: string) => {
    const newActiveTags = tags.active?.includes(tag)
      ? tags.active?.filter((t) => t !== tag)
      : [...(tags.active || []), tag];
    setActiveTagsMutation.mutate(newActiveTags);
  };

  const handleRemoveTag = (tag: string) => {
    desktop.dispatch({
      type: "confirmationWindow",
      windowId: instanceId(WindowKind.ConfirmationWindow),
      customProps: {
        message: t`Do you really want to delete tag "${tag}"`,
        callback: (confirmed: boolean) => {
          if (!confirmed) {
            return;
          }

          removeTagMutation.mutate(tag, {
            onSuccess: () => {
              dispatch(removeTagFromAllProfiles(tag));
            },
          });
        },
      },
    });
  };

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onFocusCapture={(event) => {
        // The design-system rename input doesn't expose input props.
        if (event.target instanceof HTMLInputElement) {
          event.target.maxLength = 23;
          event.target.placeholder = "Type a new name";
        }
      }}
    >
      {!tagsLoading && (
        <TagGroupWithAdd
          tags={(tags.available || []).map((tag) => (
            <Tag
              key={tag}
              color="grey"
              interactive
              editable
              selected={tags.active?.includes(tag) ?? false}
              renameLabel={`Rename ${tag}`}
              editLabel={`Rename ${tag}`}
              trailingIcon={
                <span className="material-icons-outlined" style={{ color: "var(--color-icon-error)", fontSize: 16 }}>
                  delete
                </span>
              }
              trailingIconLabel={`Action for ${tag}`}
              revealTrailingIcon
              onClick={() => handleToggleTag(tag)}
              onTrailingIconClick={() => handleRemoveTag(tag)}
              onRename={(newTag) =>
                renameTagMutation.mutate(
                  { oldTag: tag, newTag },
                  {
                    onSuccess: () => {
                      dispatch(renameTagInAllProfiles({ oldTag: tag, newTag }));
                    },
                  },
                )
              }
            >
              {tag}
            </Tag>
          ))}
          onAdd={(tagName) => addTagMutation.mutate(tagName)}
          isAddAtEnd={false}
          isSideNav={isSideNav}
        />
      )}
    </div>
  );
}
